import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, StarIcon } from 'lucide-react';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { Frame } from '../components/Frame';
import { CaseStudyArticle } from '../components/CaseStudyArticle';
import { ModelLibrary } from '../components/ModelLibrary';
import { ProjekteBlueprint } from '../components/ProjekteBlueprint';
import { cadModels } from '../data/models';
import { projects } from '../data/projects';
import { caseStudies } from '../data/caseStudies';
type ProjekteProps = {
  accent: 'violet' | 'signal';
};
type Tab = 'projekte' | 'modelle' | 'fallstudien';
const TAB_IDS: Tab[] = ['projekte', 'modelle', 'fallstudien'];
export function Projekte({
  accent
}: ProjekteProps) {
  const [tab, setTab] = useState<Tab>(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) as Tab : 'projekte';
    return TAB_IDS.includes(hash) ? hash : 'projekte';
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Projekte & Fallstudien · Konrad Walz';
  }, []);
  const select = (next: Tab) => {
    setTab(next);
    window.history.replaceState(null, '', `#${next}`);
  };
  return <div lang="de" data-accent={accent} className="min-h-screen w-full bg-white font-sans text-ink antialiased" data-id="element-707" data-mp="5o5ejngzvnqw">
      <Nav data-id="element-708" data-mp="9q0ikzqb8f8d" />
      <main data-id="element-709" data-mp="9qilgf7h9rp9">
        <Frame data-id="element-710" data-mp="9opeszwv77h9">
          <div className="relative overflow-hidden px-5 pb-20 pt-12 md:px-6 md:pb-28 md:pt-20" data-id="element-711" data-mp="9p7g4aydwbw1">
            <ProjekteBlueprint data-id="element-712" data-mp="en8yetnzk76g" />
            <div className="relative" data-id="element-713" data-mp="emh3zm64kfhr">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors duration-150 hover:text-ink" data-id="element-714" data-mp="jfa8c8xolltb">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-715" data-mp="tdznvv23uu6e" />
              Zur Startseite
            </Link>

            <header className="mt-8 max-w-[760px]" data-id="element-716" data-mp="xf1q8krbjmcl">
              <h1 className="font-display text-[40px] font-semibold leading-[1.03] tracking-[-0.04em] md:text-[60px]" data-id="element-717" data-mp="xfl0rgen38gl">
                Projekte & Fallstudien.
              </h1>
              <p className="mt-5 text-[17px] leading-relaxed text-ink/70 md:text-[18px]" data-id="element-718" data-mp="2pi5spltpe91b">
                Ein Auszug aus über 300 Aufträgen – und ausführliche Einblicke, wie aus Skizzen, Fotos und alten Plänen
                fertigungsreife CAD-Daten werden.
              </p>
            </header>

            <div role="tablist" aria-label="Ansicht" className="mt-10 inline-flex rounded-[10px] bg-canvas p-1" data-id="element-719" data-mp="3d55jy8j9w6qk">
              {([{
                id: 'projekte',
                label: 'Projekte',
                count: projects.length
              }, {
                id: 'modelle',
                label: '3D-Modelle',
                count: cadModels.length
              }, {
                id: 'fallstudien',
                label: 'Fallstudien',
                count: caseStudies.length
              }] as const).map(t => {
                const active = tab === t.id;
                return <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={active} aria-controls={`panel-${t.id}`} onClick={() => select(t.id)} className={`flex items-center gap-2 rounded-[8px] px-4 py-2.5 text-[15px] font-medium transition-colors duration-150 ${active ? 'bg-white text-ink shadow-[0_1px_3px_rgba(70,28,64,0.12)]' : 'text-muted hover:text-ink'}`} data-id="element-720" data-mp="1ntln20tll6yi">
                    {t.label}
                    <span className={`text-[13px] ${active ? 'text-muted' : 'text-muted/70'}`} data-id="element-721" data-mp="kzg1igsgr1mr">{t.count}</span>
                  </button>;
              })}
            </div>

            {tab === 'projekte' ? <section id="panel-projekte" role="tabpanel" aria-labelledby="tab-projekte" className="mt-12" data-id="element-722" data-mp="2fe8wcdlivodn">
                <ul className="grid gap-4 md:grid-cols-2 md:gap-5" data-id="element-723" data-mp="2kbiklpx6dtzw">
                  {projects.map(p => <li key={p.id} className="flex flex-col rounded-[12px] bg-white p-6 shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_10px_30px_-16px_rgba(70,28,64,0.18)] md:p-7" data-id="element-724" data-mp="k8zqgvbvmej6">
                      <div className="flex items-center justify-between gap-4" data-id="element-725" data-mp="6a9szel5acua">
                        <p className="text-[14px] text-muted" data-id="element-726" data-mp="1awxy6k4dzgm">{p.clientName}</p>
                        <div className="flex gap-0.5" role="img" aria-label={`${p.rating} von 5 Sternen`} data-id="element-727" data-mp="1czxpib2r6yj">
                          {Array.from({
                        length: 5
                      }).map((_, i) => <StarIcon key={i} aria-hidden="true" className={`h-4 w-4 ${i < p.rating ? 'fill-[#fbbc04] text-[#fbbc04]' : 'fill-line text-line'}`} data-id="element-728" data-mp="3nr16hrrhyvkf" data-mpi={i} />)}
                        </div>
                      </div>
                      <h2 className="mt-3 text-[21px] font-medium leading-snug tracking-[-0.015em] text-ink" data-id="element-729" data-mp="1p97k9ks1skda">{p.title}</h2>
                      <p className="mt-2 text-[16px] leading-relaxed text-ink/70" data-id="element-730" data-mp="1u6g0hftmyebw">{p.description}</p>
                      <p className="mt-auto pt-6 text-[14px] text-muted" data-id="element-731" data-mp="1yanr5ijnm4cr">{p.details.join(' · ')}</p>
                    </li>)}
                </ul>

                <button type="button" onClick={() => select('fallstudien')} className="mt-10 text-[15px] font-medium text-ink underline underline-offset-4 transition-colors duration-150 hover:text-ink/60" data-id="element-732" data-mp="16iq3mekhpf8a">
                  Ausführliche Fallstudien ansehen →
                </button>
              </section> : tab === 'modelle' ? <section id="panel-modelle" role="tabpanel" aria-labelledby="tab-modelle" className="mt-12" data-id="element-733" data-mp="muz46r6eh5by">
                <ModelLibrary data-id="element-734" data-mp="rs8t85pvnez9" />
              </section> : <section id="panel-fallstudien" role="tabpanel" aria-labelledby="tab-fallstudien" className="mt-12 grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-16" data-id="element-735" data-mp="1v3o8jnolgzdh">
                <nav aria-label="Fallstudien" className="hidden lg:block" data-id="element-736" data-mp="23vq5vd3d9zjj">
                  <ul className="sticky top-28 space-y-1" data-id="element-737" data-mp="1zw7lqjjddshr">
                    {caseStudies.map(s => <li key={s.id} data-id="element-738" data-mp="1c64zwl61tirl">
                        <a href={`#${s.id}`} onClick={e => {
                      e.preventDefault();
                      document.getElementById(s.id)?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    }} className="block rounded-[6px] px-3 py-2 text-[14px] leading-snug text-muted transition-colors duration-150 hover:bg-canvas hover:text-ink" data-id="element-739" data-mp="1c76hz9lhf5it">
                          {s.title}
                        </a>
                      </li>)}
                  </ul>
                </nav>
                <div data-id="element-740" data-mp="344y719hgc9gy">
                  {caseStudies.map(s => <CaseStudyArticle key={s.id} study={s} data-id="element-741" data-mp="2q4x0bdeia7tk" />)}
                </div>
              </section>}
            </div>
          </div>
        </Frame>
      </main>
      <Footer data-id="element-742" data-mp="3itm4xmersva2" />
    </div>;
}